import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json();
        setError(data.detail || 'Reset failed. The link may have expired.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <Lock size={48} style={{ color: '#8B7DA8', margin: '0 auto 16px' }} />
          <h2 className="text-lg font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif", color: '#4F378B' }}>
            Invalid Reset Link
          </h2>
          <p className="text-sm mb-4" style={{ color: '#5C5470' }}>This link is invalid or has expired.</p>
          <button onClick={() => navigate('/')} className="text-sm font-semibold" style={{ color: '#7B5EA7' }}>
            Back to Primordeia
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-sm w-full mx-auto">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-6 text-sm" style={{ color: '#5C5470' }}>
          <ArrowLeft size={16} /> Back to Primordeia
        </button>

        {success ? (
          <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
            className="rounded-3xl p-8 text-center" style={{ backgroundColor: '#E8F5E8', boxShadow: '0 12px 40px rgba(76,175,80,0.15)' }}
            data-testid="reset-success">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#4CAF50' }}>
              <Check size={28} style={{ color: '#FFF' }} />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif", color: '#2E7D32' }}>
              Password Reset!
            </h2>
            <p className="text-sm mb-4" style={{ color: '#5C5470' }}>You can now sign in with your new password.</p>
            <button onClick={() => navigate('/')}
              className="px-6 py-2.5 rounded-full text-white font-semibold text-sm"
              style={{ background: 'linear-gradient(135deg, #4F378B, #7B5EA7)' }}>
              Sign In
            </button>
          </motion.div>
        ) : (
          <div className="rounded-3xl p-8" style={{ background: 'rgba(255,255,255,0.9)', boxShadow: '0 16px 48px rgba(79,55,139,0.15)' }}
            data-testid="reset-form">
            <div className="text-center mb-6">
              <Lock size={32} style={{ color: '#4F378B', margin: '0 auto 8px' }} />
              <h2 className="text-xl font-bold" style={{ fontFamily: "'Fredoka', sans-serif", color: '#4F378B' }}>
                Set New Password
              </h2>
            </div>

            {error && <div className="text-xs text-center mb-3 p-2 rounded-lg" style={{ backgroundColor: '#FDE8E8', color: '#C0392B' }}>{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-3">
              <input type="password" placeholder="New password" value={password} onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border text-sm" style={{ borderColor: '#E8E0F0' }}
                data-testid="reset-password-input" required />
              <input type="password" placeholder="Confirm password" value={confirm} onChange={e => setConfirm(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border text-sm" style={{ borderColor: '#E8E0F0' }}
                data-testid="reset-confirm-input" required />
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm"
                style={{ background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)', opacity: loading ? 0.7 : 1 }}
                data-testid="reset-submit-button">
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;
